import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const inputCls = "w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors";

function byName(items, value) {
    const needle = (value || "").trim().toLowerCase();
    if (!needle) return null;
    return items.find((item) => (item?.name || "").trim().toLowerCase() === needle) || null;
}

export default function LocationFields({ form, setForm, countryLabel = "Country", stateLabel = "State", cityLabel = "City" }) {
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);

    const selectedCountry = useMemo(() => byName(countries, form.country), [countries, form.country]);
    const selectedState = useMemo(() => byName(states, form.state), [states, form.state]);

    useEffect(() => {
        let alive = true;
        const loadCountries = async () => {
            setLoadingCountries(true);
            try {
                const { data } = await api.get("/locations/countries?limit=300");
                if (!alive) return;
                setCountries(Array.isArray(data?.items) ? data.items : []);
            } catch {
                if (alive) setCountries([]);
            } finally {
                if (alive) setLoadingCountries(false);
            }
        };
        loadCountries();
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        let alive = true;
        const loadStates = async () => {
            if (!selectedCountry?.id) {
                setStates([]);
                setCities([]);
                return;
            }
            setLoadingStates(true);
            try {
                const { data } = await api.get(`/locations/states?country_id=${selectedCountry.id}&limit=5000`);
                if (!alive) return;
                setStates(Array.isArray(data?.items) ? data.items : []);
            } catch {
                if (alive) setStates([]);
            } finally {
                if (alive) setLoadingStates(false);
            }
        };
        loadStates();
        return () => { alive = false; };
    }, [selectedCountry?.id]);

    useEffect(() => {
        let alive = true;
        const loadCities = async () => {
            if (!selectedState?.id) {
                setCities([]);
                return;
            }
            setLoadingCities(true);
            try {
                const { data } = await api.get(`/locations/cities?state_id=${selectedState.id}&limit=10000`);
                if (!alive) return;
                setCities(Array.isArray(data?.items) ? data.items : []);
            } catch {
                if (alive) setCities([]);
            } finally {
                if (alive) setLoadingCities(false);
            }
        };
        loadCities();
        return () => { alive = false; };
    }, [selectedState?.id]);

    const setField = (patch) => setForm({ ...form, ...patch });

    return (
        <>
            <label className="space-y-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">{countryLabel}</span>
                <select
                    value={form.country || ""}
                    onChange={(e) => setField({ country: e.target.value, state: "", city: "" })}
                    className={inputCls}
                    disabled={loadingCountries && countries.length === 0}
                >
                    <option value="">{loadingCountries ? "Loading countries..." : "Select country"}</option>
                    {countries.map((country) => (
                        <option key={country.id} value={country.name}>
                            {country.name}
                        </option>
                    ))}
                </select>
            </label>

            <label className="space-y-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">{stateLabel}</span>
                <select
                    value={form.state || ""}
                    onChange={(e) => setField({ state: e.target.value, city: "" })}
                    className={inputCls}
                    disabled={!selectedCountry?.id || loadingStates}
                >
                    <option value="">{!selectedCountry?.id ? "Select country first" : loadingStates ? "Loading states..." : "Select state"}</option>
                    {states.map((state) => (
                        <option key={state.id} value={state.name}>
                            {state.name}
                        </option>
                    ))}
                </select>
            </label>

            <label className="space-y-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">{cityLabel}</span>
                <select
                    value={form.city || ""}
                    onChange={(e) => setField({ city: e.target.value })}
                    className={inputCls}
                    disabled={!selectedState?.id || loadingCities}
                >
                    <option value="">{!selectedState?.id ? "Select state first" : loadingCities ? "Loading cities..." : "Select city"}</option>
                    {cities.map((city) => (
                        <option key={city.id} value={city.name}>
                            {city.name}
                        </option>
                    ))}
                </select>
            </label>
        </>
    );
}
